import { createTheme } from '@mui/material/styles';

const compactTheme = createTheme({
    spacing: 4, // default is 8; maximum density
    typography: {
        fontSize: 12,
        h4: { fontSize: '1.1rem', marginBottom: '0.15rem', lineHeight: 1.2 },
        h5: { fontSize: '0.95rem', marginBottom: '0.1rem', lineHeight: 1.2 },
        h6: { fontSize: '0.85rem', marginBottom: '0.1rem', lineHeight: 1.2 },
        body1: { fontSize: '0.78rem', lineHeight: 1.3 },
        body2: { fontSize: '0.75rem', lineHeight: 1.3 },
        subtitle1: { fontSize: '0.8rem', lineHeight: 1.3 },
        subtitle2: { fontSize: '0.75rem', lineHeight: 1.3 },
        caption: { fontSize: '0.7rem' },
    },
    components: {
        // ── Tables ──────────────────────────────────
        MuiTable: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                root: {
                    width: '100%',
                    tableLayout: 'auto',
                    borderSpacing: 0,
                    borderCollapse: 'collapse',
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: { padding: 0 },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '2px 4px',
                    fontSize: '0.78rem',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                },
                head: {
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    padding: '3px 4px',
                    lineHeight: 1.3,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: { height: 'auto' },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: { minHeight: 28, overflow: 'visible', width: '100%' },
                toolbar: { minHeight: 28, paddingLeft: 0, paddingRight: 4, justifyContent: 'flex-start' },
                spacer: { display: 'none' },
                selectLabel: { fontSize: '0.75rem' },
                displayedRows: { fontSize: '0.75rem' },
                select: { fontSize: '0.75rem' },
            },
        },

        // ── Form controls ──────────────────────────
        MuiTextField: {
            defaultProps: { size: 'small', margin: 'none' },
        },
        MuiSelect: {
            defaultProps: { size: 'small', margin: 'dense' },
        },
        MuiFormControl: {
            defaultProps: { size: 'small', margin: 'none' },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: { marginLeft: 0, marginRight: 8 },
                label: { fontSize: '0.78rem' },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: { fontSize: '0.78rem' },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: { fontSize: '0.78rem' },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: { borderRadius: 3 },
                input: { padding: '4px 8px' },
            },
        },

        // ── Buttons ────────────────────────────────
        MuiButton: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    minHeight: 26,
                    lineHeight: 1.4,
                },
                sizeSmall: {
                    padding: '2px 8px',
                },
            },
        },
        MuiButtonGroup: {
            defaultProps: { size: 'small' },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { padding: 4 },
                sizeSmall: { padding: 2 },
            },
        },

        // ── Cards / Paper ──────────────────────────
        MuiPaper: {
            styleOverrides: {
                root: { padding: 0 },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '4px 8px',
                    '&:last-child': { paddingBottom: '4px' },
                },
            },
        },

        // ── Chips ──────────────────────────────────
        MuiChip: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                root: { height: 20, fontSize: '0.7rem' },
                sizeSmall: { height: 18 },
            },
        },

        // ── Dialogs ────────────────────────────────
        MuiDialogTitle: {
            styleOverrides: {
                root: { padding: '8px 12px', fontSize: '0.95rem' },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: { padding: '6px 12px' },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: { padding: '4px 12px' },
            },
        },

        // ── Grid ───────────────────────────────────
        MuiGrid: {
            styleOverrides: {
                root: { '& > .MuiGrid-item': { paddingTop: '4px' } },
            },
        },

        // ── Stack ──────────────────────────────────
        MuiStack: {
            defaultProps: { spacing: 0.5 },
        },

        // ── Toolbar ────────────────────────────────
        MuiToolbar: {
            styleOverrides: {
                root: { minHeight: 28 },
                dense: { minHeight: 28 },
            },
        },

        // ── Tabs ───────────────────────────────────
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: '0.78rem',
                    minHeight: 32,
                    padding: '4px 8px',
                    textTransform: 'none',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: { minHeight: 32 },
            },
        },

        // ── Switch ─────────────────────────────────
        MuiSwitch: {
            defaultProps: { size: 'small' },
        },

        // ── Accordion ──────────────────────────────
        MuiAccordionSummary: {
            styleOverrides: {
                root: { minHeight: 32, '&.Mui-expanded': { minHeight: 32 } },
                content: { margin: '4px 0', '&.Mui-expanded': { margin: '4px 0' } },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: { padding: '4px 8px 8px' },
            },
        },
    },
});

export default compactTheme;
